import { SUPPORTED_LOCALES, type Locale } from './types';

/**
 * How a language presents itself to a visitor who does not yet speak the
 * page's language: in its own words.
 *
 * The gate is the one screen a visitor sees BEFORE picking a language, so
 * every label here is deliberately in the target language — "Deutsch", not
 * "German". Translating the list into the current UI locale would be the
 * one place where translating is wrong.
 */
export const LANGUAGE_NAMES: Record<Locale, string> = {
    de_DE: 'Deutsch',
    en_US: 'English',
    fr_FR: 'Français',
    es_ES: 'Español',
    it_IT: 'Italiano',
    nl_NL: 'Nederlands',
};

/**
 * Flag per language — drawn inline rather than loaded, because the gate is
 * the first thing a visitor sees and a flag that arrives late (or not at
 * all, behind a blocked CDN) makes the whole screen look broken. Six small
 * SVGs cost less than one sprite request.
 *
 * English shows the Union Jack even though the locale code is `en_US`. The
 * code is an artefact of the locale list the admin ships; the visitors these
 * configurators serve are European, and in a European shop the Union Jack is
 * what reads as "English". The written name next to it carries the actual
 * meaning either way — which is exactly why the gate shows both.
 */
export const FLAG_PATHS: Record<Locale, { viewBox: string; shapes: string }> = {
    // Three horizontal bands.
    de_DE: {
        viewBox: '0 0 5 3',
        shapes:
            '<rect width="5" height="3" fill="#000"/>' +
            '<rect width="5" height="2" y="1" fill="#D00"/>' +
            '<rect width="5" height="1" y="2" fill="#FFCE00"/>',
    },
    fr_FR: {
        viewBox: '0 0 3 2',
        shapes:
            '<rect width="3" height="2" fill="#FFF"/>' +
            '<rect width="1" height="2" fill="#002395"/>' +
            '<rect width="1" height="2" x="2" fill="#ED2939"/>',
    },
    it_IT: {
        viewBox: '0 0 3 2',
        shapes:
            '<rect width="3" height="2" fill="#FFF"/>' +
            '<rect width="1" height="2" fill="#009246"/>' +
            '<rect width="1" height="2" x="2" fill="#CE2B37"/>',
    },
    nl_NL: {
        viewBox: '0 0 9 6',
        shapes:
            '<rect width="9" height="6" fill="#FFF"/>' +
            '<rect width="9" height="2" fill="#AE1C28"/>' +
            '<rect width="9" height="2" y="4" fill="#21468B"/>',
    },
    // Spain: red-yellow-red, the middle band twice as tall. The coat of arms
    // is left out on purpose — at tile size it is a smudge, and drawing it
    // badly is worse than not drawing it.
    es_ES: {
        viewBox: '0 0 3 2',
        shapes:
            '<rect width="3" height="2" fill="#AA151B"/>' +
            '<rect width="3" height="1" y="0.5" fill="#F1BF00"/>',
    },
    // Union Jack, simplified: the white saltire and the red saltire are drawn
    // as straight diagonals rather than the real offset pinwheel. At 32 px the
    // difference is invisible and the path stays short.
    en_US: {
        viewBox: '0 0 60 30',
        shapes:
            '<rect width="60" height="30" fill="#012169"/>' +
            '<path d="M0,0 60,30 M60,0 0,30" stroke="#FFF" stroke-width="6"/>' +
            '<path d="M0,0 60,30 M60,0 0,30" stroke="#C8102E" stroke-width="4"/>' +
            '<path d="M30,0 V30 M0,15 H60" stroke="#FFF" stroke-width="10"/>' +
            '<path d="M30,0 V30 M0,15 H60" stroke="#C8102E" stroke-width="6"/>',
    },
};

/**
 * Narrow an arbitrary locale code from component data (`de_DE`, `de-DE`,
 * `de`, `EN`) to one of the locales the gate can label and draw.
 *
 * Deliberately NOT the same as {@link import('./resolve').resolveLocale}:
 * that one always returns something (falling back to `en_US`), which is
 * right for picking UI strings and wrong here — a component language the
 * gate cannot name must drop out of the list rather than turn into English.
 */
export function matchGateLocale(raw: string | undefined | null): Locale | undefined {
    if (!raw) return undefined;
    const normalised = String(raw).replace(/[-.]/g, '_').toLowerCase();
    for (const supported of SUPPORTED_LOCALES) {
        if (supported.toLowerCase() === normalised) return supported;
    }
    const lang = normalised.split('_')[0];
    for (const supported of SUPPORTED_LOCALES) {
        if (supported.toLowerCase().startsWith(lang + '_')) return supported;
    }
    return undefined;
}
